import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { AddRecipientModal } from '../../message-modals/add-recipient/add-recipient';
import {
  FormBuilder,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ContactsService } from '../../../services/contacts/contacts.service';
import { PaginationComponent } from '../../inputs/pagination/pagination.component';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { DataTableComponent } from '../../ui/data-table/data-table.component';

@Component({
  selector: 'add-contact-modal',
  standalone: true,
  imports: [
    MatIcon,
    CommonModule,
    AddRecipientModal,
    FormsModule,
    ReactiveFormsModule,
    PaginationComponent,
    DataTableComponent,
  ],
  templateUrl: './add-contacts-component.html',
  styleUrls: ['./add-contact-modal.css'],
})
export class AddContactModel {
  @Input() data: any[] = []; // Pass data from parent
  @Input() add_recipient: boolean = false;
  @Output() close = new EventEmitter<void>();
  @Output() selectedContactsChange = new EventEmitter<any[]>();
  @Input() selectedContacts: any[] = [];

  closeModal(): void {
    this.close.emit();
  }

  // Form Control for search input
  searchControl = new FormControl('');
  contact_list: any[] = [];
  allSelectedContacts = false; // Tracks if all contacts are selected
  searchParam: string = '';

  constructor(
    private router: Router,
    private builder: FormBuilder,
    private fb: FormBuilder
  ) {}

  contactsService = inject(ContactsService);

  // Fetch contacts from the service based on the search query
  fetchContacts(): void {
    this.contactsService.fetchContacts({
      'fullName.contains': this.searchParam,
    });

    this.contactsService.getContacts.subscribe({
      next: (data) => {
        this.contact_list = data.map((contact) => ({
          ...contact,
          selected: this.selectedContacts.some(
            (selected) => selected.uuid === contact.uuid
          ),
        }));
      },
      error: (err) => {
      },
    });
  }

  // Update search query and fetch new contacts
  getSearchParam(e: any): void {
    this.searchParam = e;
    this.fetchContacts();
  }

  // Toggle selection of all contacts
  toggleAllContacts(event: any): void {
    this.allSelectedContacts = event.target.checked;
    this.contact_list.forEach((contact: any) => {
      contact.selected = this.allSelectedContacts;
    });
    this.updateSelectedContacts();
  }

  // Update selected contacts based on checkbox state
  checkSelectionContact(): void {
    this.updateSelectedContacts();
  }

  // Update selectedContacts array and emit to parent component
  updateSelectedContacts(): void {
    this.selectedContacts = this.contact_list.filter(
      (contact: any) => contact.selected
    );
    this.selectedContactsChange.emit([...this.selectedContacts]); // Emit new reference
  }

  // Persist selected contacts in localStorage
  persistSelectedContacts(): void {
    localStorage.setItem(
      'selectedContacts',
      JSON.stringify(this.selectedContacts)
    );
  }

  // Load previously selected contacts from localStorage
  loadSelectedContacts(): void {
    const savedContacts = localStorage.getItem('selectedContacts');
    if (savedContacts) {
      this.selectedContacts = JSON.parse(savedContacts);
      this.contact_list = this.contact_list.map((contact) => ({
        ...contact,
        selected: this.selectedContacts.some(
          (selectedContact) => selectedContact.uuid === contact.uuid
        ),
      }));
    }
  }

  // Filter data based on search term
  filterData(searchTerm: string, param = 'message_subject'): any[] {
    if (!searchTerm) {
      return this.data;
    }
    return this.data.filter((item) =>
      item[param].toLowerCase().includes(searchTerm.toLowerCase())
    );
  }

  ngOnInit(): void {
    this.fetchContacts();

    // Reactive search input listener
    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged())
      .subscribe((value: any) => {
        this.searchParam = value;
        this.fetchContacts();
      });

    // Load previously selected contacts
    this.loadSelectedContacts();
  }

  // ngOnDestroy(): void {
  //   this.persistSelectedContacts(); // Persist selected contacts on component destroy
  // }

  ngOnChanges(): void {
    // Update selected state of contacts when selectedContacts input changes
    this.contact_list.forEach((contact) => {
      contact.selected = this.selectedContacts.some(
        (selectedContact) => selectedContact.uuid === contact.uuid
      );
    });
  }
}
